import { useEffect, useRef, useState } from 'react'
import { PexIcon } from './icons/PexIcon'
import { useT } from '../i18n'
import { orgContextI18n } from '../i18n/shared/org-context'
import { resolveCurrentUser } from '../lib/user-permissions'
import { useOrgContextStore } from '../store/orgContext'
import { useEnterOrgView } from './useEnterOrgView'
import { OrgSearchModal } from './OrgSearchModal'
import type { DirectoryOrg } from '../mocks/data/organizations'
import type { OrgViewSide } from '../lib/orgViewState'

// Platform Admin Organization Context switcher (D-5 Ring 3). Replaces the dev
// role select for the admin shell. Gated by platform.access_seller_orgs /
// platform.access_vendor_orgs capabilities; an admin lacking both never sees it.

export function OrgContextSwitcher() {
  const t = useT(orgContextI18n)
  const { mode, viewing } = useOrgContextStore()
  const enterOrgView = useEnterOrgView()
  const [menuOpen, setMenuOpen] = useState(false)
  const [modalSide, setModalSide] = useState<OrgViewSide | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  const me = resolveCurrentUser({ role: 'admin' })
  const canSeller = me.effective_capabilities.includes('platform.access_seller_orgs')
  const canVendor = me.effective_capabilities.includes('platform.access_vendor_orgs')

  useEffect(() => {
    if (!menuOpen) return
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [menuOpen])

  if (!canSeller && !canVendor) return null

  // While viewing an org, the banner owns the exit affordance; keep the switcher
  // compact (no menu) so the platform admin sees a single clear context.
  if (mode === 'platform_viewing' && viewing) {
    return (
      <span className="flex items-center gap-1.5 rounded-md border border-[var(--sidebar-divider)] bg-[var(--sidebar-control-bg)] px-2 py-1 text-xs text-[var(--sidebar-parent-text)]">
        <PexIcon name="verified" className="h-[13px] w-[13px]" size="xs" tone="inherit" />
        {viewing.orgName}
      </span>
    )
  }

  const openSide = (side: OrgViewSide) => {
    setMenuOpen(false)
    setModalSide(side)
  }

  const handleSelect = (org: DirectoryOrg) => {
    enterOrgView?.(org)
    setModalSide(null)
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 rounded-md border border-[var(--sidebar-divider)] bg-[var(--sidebar-control-bg)] px-2.5 py-1.5 text-sm text-[var(--sidebar-parent-text)] hover:bg-[var(--sidebar-control-hover-bg)] focus-visible:outline-none focus-visible:shadow-[var(--sidebar-focus-shadow)]"
      >
        <PexIcon name="verified" className="h-[14px] w-[14px] text-emerald-600" size="xs" tone="inherit" />
        <span className="flex-1 text-left">{t.switcher.orgAccessLabel}</span>
        <PexIcon name="caretDown" className="h-[14px] w-[14px] text-[var(--sidebar-icon)]" size="xs" tone="inherit" />
      </button>

      {menuOpen && (
        <div className="absolute left-0 z-50 mt-1 w-full overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-float)]">
          <div className="border-b border-slate-100 appearance-dark:border-[var(--color-border)] px-3 py-2 text-[11px] text-slate-500 appearance-dark:text-[var(--color-text-muted)]">
            {t.switcher.platformAdminHint}
          </div>
          {canSeller && (
            <button
              type="button"
              onClick={() => openSide('seller')}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-slate-700 appearance-dark:text-[var(--color-text)] hover:bg-slate-50 appearance-dark:hover:bg-[var(--color-surface-muted)]"
            >
              <PexIcon name="store" className="h-[15px] w-[15px] text-indigo-600" size="xs" tone="inherit" />
              {t.switcher.openSellerDashboard}
            </button>
          )}
          {canVendor && (
            <button
              type="button"
              onClick={() => openSide('supply')}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-slate-700 appearance-dark:text-[var(--color-text)] hover:bg-slate-50 appearance-dark:hover:bg-[var(--color-surface-muted)]"
            >
              <PexIcon name="factory" className="h-[15px] w-[15px] text-amber-600" size="xs" tone="inherit" />
              {t.switcher.openVendorDashboard}
            </button>
          )}
          <div className="border-t border-slate-100 appearance-dark:border-[var(--color-border)] px-3 py-2 text-[11px] text-slate-400 appearance-dark:text-[var(--color-text-muted)]">
            <PexIcon name="signOut" className="h-[11px] w-[11px] mr-1 inline" size="xs" tone="inherit" />
            {t.switcher.exitHint}
          </div>
        </div>
      )}

      <OrgSearchModal
        side={modalSide}
        open={modalSide !== null}
        onClose={() => setModalSide(null)}
        onSelect={handleSelect}
      />
    </div>
  )
}
